import { ITextNoteFont, Rectangle } from './interfaces';
import { environment } from 'src/environments/environment';
import { AnnotationTypes, CustomDocviewerCursorStyles, HighlightingTypes } from './pdf-utils/enums';
import { ReadingEcho } from './reading-utils/enums';
import { MimeTypesEnum } from '../utils/enums';
import { Core, WebViewerInstance, WebViewerOptions } from '@pdftron/webviewer';
import { CustomEpubCursorStyles } from './epub-utils/enums';
import { epubCursorBase64Map } from './epub-utils/cursors';
import DOMPurify from 'dompurify';


export function createGuid(): string {
  const array = new Uint32Array(8);
  window.crypto.getRandomValues(array);
  let str = '';
  for (let i = 0; i < array.length; i++) {
    str += (i < 2 || i > 5 ? '' : '-') + array[i].toString(16).slice(-4);
  }
  return str;
}

export function hexToRgb(hex: string): any | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function isRectContainsPoint(x: number, y: number, rect: Rectangle | null): boolean {
  if (!x || !y || !rect) {
    return false;
  }

  return x >= rect.x && x < rect.x + rect.width &&
    y >= rect.y && y < rect.y + rect.height;
}

export function isRectIntersectRect(rect1: Rectangle | null, rect2: Rectangle): boolean {
  if (!rect1 || !rect2) {
    return false;
  }

  const margin = 2;
  return rect2.x < rect1.x + rect1.width - margin &&
    rect1.x < rect2.x + rect2.width - margin &&
    rect2.y < rect1.y + rect1.height - margin &&
    rect1.y < rect2.y + rect2.height - margin;
}

export function getProductName(): string {
  if (isK3000domain()) {
    return environment.auth.K3000Product;
  } else {
    return environment.auth.AlineaProduct;
  }
}

export function getDeviceName(): string {
  return 'ASGARD';
}

export function debounce(
  cntxt: any,
  timeout: number,
  callback: (...args) => any,
  cancelable: boolean = false,
  ...callbackArgs: any[]): (...args: any[]) => void {
  let waitingFlag = false;
  let timerId: number | null = null;

  return (...args) => {
    if (cancelable && timerId !== null) {
      clearTimeout(timerId);
      waitingFlag = false;
    }

    if (waitingFlag) {
      return;
    }

    waitingFlag = true;

    timerId = setTimeout((() => {
      callback.apply(cntxt, callbackArgs.concat(args));
      waitingFlag = false;
      timerId = null;
    }) as TimerHandler, timeout);
  };
}

export function isK3000domain(): boolean {
  const k3000domain = 'k3000';
  const dns = window.location.href;

  return dns.indexOf(k3000domain) > -1;
}

export function convertSettingsColorToRgb(instance: WebViewerInstance, color: string, opacity: number = 0.95): Core.Annotations.Color {
  return new instance.Core.Annotations.Color(
    hexToRgb(color).r,
    hexToRgb(color).g,
    hexToRgb(color).b,
    opacity
  );
}

export function isAnnotationType(anot: Core.Annotations.Annotation, annotTypes: AnnotationTypes[], checkIntent: boolean = false): boolean {
  if (anot) {
    let result = annotTypes.find(e => anot.Subject === e || (checkIntent && !!anot['getIntent'] ? ((anot as Core.Annotations.FreeTextAnnotation).getIntent() === e) : false));

    // for older versions of pdftron, the subject can be null
    // (intent is also null)
    // so, check the type instead
    if (!anot.Subject && !anot['getIntent']) {
      const type = anot['_xsi:type'];
      result = annotTypes.find(e => type ? type === e : false);
    }

    // return result
    if (result) {
      return true;
    }
  }
  return false;
}

export const textNotesFonts: ITextNoteFont[] = [
  {
    title: 'Arial',
    value: '"Arial", Helvetica, sans-serif'
  },
  {
    title: 'Courier',
    value: '"Courier" ,Courier, monospace'
  },
  {
    title: 'Georgia',
    value: '"Georgia", serif'
  },
  {
    title: 'Helvetica',
    value: '"Helvetica", Helvetica, sans-serif'
  },
  {
    title: 'Impact',
    value: '"Impact", fantasy'
  },
  {
    title: 'Times New Roman',
    value: '"Times New Roman", Times, serif'
  },
  {
    title: 'Verdana',
    value: '"Verdana", Geneva, sans-serif'
  }
];

export const textNotesFontSizes = [
  8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
  20, 21, 22, 23, 24, 25, 26, 27, 28, 29,
  30, 31, 32, 33, 34, 35, 36, 37, 38, 39,
  40, 41, 42, 43, 44, 45, 46, 47, 48, 49,
  50, 51, 52, 53, 54, 55, 56, 57, 58, 59,
  60, 61, 62, 63, 64, 65, 66, 67, 68, 69,
  70, 71, 72
];

export function realClickedPage(x: number, y: number, instance: WebViewerInstance, frame: HTMLIFrameElement): number | null {
  const pagesCount = instance.Core.documentViewer.getPageCount();
  const currentIdx = instance.Core.documentViewer.getCurrentPage();
  const prevIdx = currentIdx > 0 ? currentIdx - 1 : null;
  const nextIdx = pagesCount === currentIdx ? null : currentIdx + 1;
  let currentPageContainer = getPageContainerRect(currentIdx, frame);
  if (isRectContainsPoint(x, y, currentPageContainer as DOMRect)) {
    return currentIdx;
  }
  if (prevIdx) {
    const prevPageContainer = getPageContainerRect(prevIdx, frame);
    if (isRectContainsPoint(x, y, prevPageContainer as DOMRect)) {
      return prevIdx;
    }
  }
  if (nextIdx) {
    const nextPageContainer = getPageContainerRect(nextIdx, frame);
    if (isRectContainsPoint(x, y, nextPageContainer as DOMRect)) {
      return nextIdx;
    }
  }
  for (let i = 0; i <= pagesCount; i++) {
    currentPageContainer = getPageContainerRect(i, frame);
    if (isRectContainsPoint(x, y, currentPageContainer as DOMRect)) {
      return i;
    }
  }
  return null;
}

export function getPageContainerRect(pageIdx: number | null, frame: HTMLIFrameElement): DOMRect | null {
  if (frame && frame.contentDocument && pageIdx !== null) {
    const page = frame.contentDocument.querySelector(`#pageContainer${pageIdx}`); // zero-indexed page index
    if (page) {
      const pageRect = page.getBoundingClientRect();
      return pageRect as DOMRect;
    }
  }
  return null;
}

export function annotRotation(annotation: Core.Annotations.Annotation, rotation: number): Core.Annotations.Annotation {
  annotation.Rotation = rotation * 90;
  if (rotation === 2 || rotation === 0) {
    if (rotation === 2) {
      annotation.X = annotation.X - annotation.Width;
      annotation.Y = annotation.Y - annotation.Height;
    }
  } else if (rotation === 1 || rotation === 3) {
    if (rotation === 3) {
      annotation.X = annotation.X - annotation.Height;
    } else if (rotation === 1) {
      annotation.Y = annotation.Y - annotation.Width;
    }
    const prevWidth = annotation.Width;
    annotation.Width = annotation.Height;
    annotation.Height = prevWidth;
  }
  return annotation;
}

export function getAnnotationRectangle(annotation: Core.Annotations.Annotation, xOffset: number, pageHeight: number): Rectangle | null {
  if (!annotation) {
    return null;
  }

  const height = annotation.getHeight();

  return {
    x: annotation.getX() + xOffset,
    y: pageHeight - annotation.getY() - height,
    width: annotation.getWidth(),
    height
  };
}

export function getFroalaIframes(): HTMLCollection | null {
  const froalaFrames = window.document.getElementsByClassName('fr-iframe') as HTMLCollection;

  if (froalaFrames) {
    return froalaFrames as HTMLCollection;
  }

  return null;
}

export function documentElement(documentId: string): HTMLElement | null {
  const iframe = getViewerIframe(documentId);
  if (!iframe || !iframe.contentDocument) {
    return null;
  }
  return iframe.contentDocument.querySelector('.DocumentContainer > .document') as HTMLElement;
}

function getEpubIframes(): HTMLIFrameElement[] {
  return Array.from(document.querySelectorAll<HTMLIFrameElement>(`div.colibrio-viewer iframe`));
}

function applyEpubStyle(dataAttr: string, styleContent: string): void {
  const iframes = getEpubIframes();
  iframes.forEach((iframe) => {
    const doc = iframe.contentDocument;
    if (!doc) return;
    const styleEl = doc.createElement("style");
    styleEl.innerHTML = styleContent;
    styleEl.setAttribute(dataAttr, "true");
    doc.head.appendChild(styleEl);
  });
}

function resetEpubStyle(dataAttr: string): void {
  const iframes = getEpubIframes();
  iframes.forEach((iframe) => {
    const doc = iframe.contentDocument;
    if (!doc) return;
    const styles = doc.querySelectorAll(`style[${dataAttr}]`);
    styles.forEach((style) => style.remove());
  });
}

export function updateEpubCursorIcon(cursor: CustomEpubCursorStyles, disablePointerEvents = false): void {
  const base64Cursor = epubCursorBase64Map[cursor];
  resetEpubStyle("data-epub-cursor-icon");
  applyEpubStyle("data-epub-cursor-icon",
    ` html, body {
        cursor: url("${base64Cursor}") 0 5, auto !important;
        ${disablePointerEvents ? 'pointer-events: none;' : ''}
      } `
  );
}

export function setEpubSelectionColor(color: string): void {
  resetEpubStyle("data-epub-selection-color");
  applyEpubStyle("data-epub-selection-color",
    ` html, body {
        ::selection {
          background: ${color};
          color: black;
        }
      }`
  );
}

export function resetEpubCursorIcon(): void {
  resetEpubStyle("data-epub-cursor-icon");
}

export function resetEpubSelectionColor(): void {
  resetEpubStyle("data-epub-selection-color");
}

export function updateCursorIcon(documentId: string, cursor: CustomDocviewerCursorStyles): void {
  const documentEl = documentElement(documentId);
  if (!documentEl) {
    return;
  }
  resetCursorIcon(documentId);
  documentEl.classList.add(`cursor-${cursor}`);
}

export function resetCursorIcon(documentId: string, cursor?: CustomDocviewerCursorStyles): void {
  const documentEl = documentElement(documentId);
  if (!documentEl) {
    return;
  }

  if (cursor) {
    documentEl.classList.remove(`cursor-${cursor}`);
    return;
  }

  const regexp = new RegExp('cursor-[a-zA-Z]+');
  const classesToRemove: string[] = [];
  documentEl.classList.forEach(className => {
    if (regexp.test(className)) {
      classesToRemove.push(className);
    }
  });
  documentEl.classList.remove(...classesToRemove);
}

export function aplliedCursorStyle(documentId: string): CustomDocviewerCursorStyles | null {
  const regexp = new RegExp('cursor-[a-zA-Z]+');
  const documentEl = documentElement(documentId);
  if (!documentEl) {
    return null;
  }

  let result: CustomDocviewerCursorStyles | null = null;
  documentEl.classList.forEach(item => {
    if (regexp.test(item)) {
      result = item as CustomDocviewerCursorStyles;
    }
  });

  // for (let item of documentEl.classList){
  //   if (regexp.test(item)) {
  //     return item as CustomDocviewerCursorStyles;
  //   }
  // }
  // return null;

  return result;
}

export function getViewerIframe(documentId: string): HTMLIFrameElement | null {
  return (window.document.querySelector(`div.viewer[viewerid="${documentId}"] > iframe`)) as HTMLIFrameElement;
}

// export const webViewerInternalElement = document.getElementById('webviewerInternal') as HTMLIFrameElement;
// export const webViewerInternalOpts: WebViewerOptions = {
//   path: window.origin + '/lib/pdftron', fullAPI: true, preloadWorker: 'pdf',
//   backendType: 'ems', licenseKey: environment.pdfTronLicenceKey
// };
export const webViewerDediconPrintOpts: WebViewerOptions = {
  path: window.origin + '/lib/pdftron', fullAPI: false, backendType: 'ems', licenseKey: environment.pdfTronLicenceKey
};

// space and enter event triggers for spellchecker and wordpredictor
const codes = ['space', 'enter'];
const keyCodes = [32, 13];
const keys = [' ', 'Enter'];

export function isSymbolSpaceOrEnter(code: string | number): boolean {
  return !!code && !!codes.find(c => c === (code as string).toLowerCase()) ||
    !!keyCodes.find(c => code === c) ||
    !!keys.find(c => code === c);
}

export function isSymbolSpace(code: string | number): boolean {
  return !!code && !!codes.find(c => c === (code as string).toLowerCase()) ||
    !!keyCodes.find(c => code === c) ||
    !!keys.find(c => code === c);
}

export function isSafary(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function matchReadingEchoWithSettings(settings: ReadingEcho, readingItem: ReadingEcho): boolean {
  return settings === readingItem;
}

export function filterAnnotationExceptHighlights(e: Core.Annotations.Annotation): boolean {
  return !(e.Author && (e.Author === HighlightingTypes.Word || e.Author === HighlightingTypes.Sentence));

}

export function getMimeTypeFromFile(fileName: string): MimeTypesEnum {
  if (fileName.endsWith('.rtf')) {
    return MimeTypesEnum.ApplicationRtf;
  } else if (fileName.endsWith('.pdf')) {
    return MimeTypesEnum.ApplicationPdf;
  } else if (fileName.endsWith('.kes') || fileName.endsWith('.sek')) {
    return MimeTypesEnum.KesNoType;
  } else if (fileName.endsWith('.png')) {
    return MimeTypesEnum.ImagePng;
  } else if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg')) {
    return MimeTypesEnum.ImageJpeg;
  } else if (fileName.endsWith('.docx')) {
    return MimeTypesEnum.ApplicationMSWordOpenXML;
  } else if (fileName.endsWith('.xlsx')) {
    return MimeTypesEnum.ApplicationMsExcelOpenXML;
  } else if (fileName.endsWith('.pptx')) {
    return MimeTypesEnum.ApplicationMsPptOpenXML;
  } else if (fileName.endsWith('.epub')) {
    return MimeTypesEnum.ApplicationEpub
  } else if (fileName.endsWith('.alineabook')) {
    return MimeTypesEnum.ApplicationBook;
  }

  return MimeTypesEnum.TextHtml;
}

export function parseExtenstion(val: string): string {
  const r = /(?:\.([^.]+))?$/.exec(val);
  if (r !== null && r.length !== 0) {
    return r[0];
  }
  return '';
}


export function isDocHashCustomDoc(docHash: string): boolean {
  if (docHash) {
    return (docHash.indexOf('-') > -1);
  }
  return false;
}

export function generateCustomDocumentName(text: string): string {
  const maxLenTitle = 15;
  const words = text.split(' ');
  let fileName = '';
  if (text) {
    for (let index = 0; index < words.length; index++) {
      if (index > 2) {
        break;
      } else {
        fileName += (words[index] + ' ');
      }
    }
  }
  return (fileName.substring(0, maxLenTitle) + '...');
}

export function getDistance(x1: number, y1: number, x1b: number, y1b: number, x2: number, y2: number, x2b: number, y2b: number): number {
  const left = x2b < x1;
  const right = x1b < x2;
  const bottom = y2b < y1;
  const top = y1b < y2;

  if (top && left) {
    return distance(x1, y1b, x2b, y2);
  }

  if (left && bottom) {
    return distance(x1, y1, x2b, y2b);
  }

  if (bottom && right) {
    return distance(x1b, y1, x2, y2b);
  }

  if (right && top) {
    return distance(x1b, y1b, x2, y2);
  }

  if (left) {
    return x1 - x2b;
  }

  if (right) {
    return x2 - x1b;
  }

  if (bottom) {
    return y1 - y2b;
  }

  if (top) {
    return y2 - y1b;
  }

  return 0;
}

function distance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt(Math.pow((x1 - x2), 2) + Math.pow((y1 - y2), 2));
}

/**
 * Sanitizes an HTML string, removing dangerous content but keeping safe tags.
 * @param html The HTML string to sanitize.
 * @returns The sanitized HTML string.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html);
}
